/**
 * Analytics Sahha — wrapper Plausible + GA4 + PostHog typé.
 *
 * Usage côté client :
 *   track("lead:submitted", { type: "APPOINTMENT_REQUEST", specialty: "dermatologue" });
 *
 * Les noms d'événements sont centralisés pour garantir qu'aucun typo
 * ne crée un "nouvel event" fantôme côté dashboard.
 */
import posthog from "posthog-js";

export type SahhaEvent =
  | "booking:started"
  | "booking:slot_selected"
  | "booking:submitted"
  | "booking:confirmed"
  | "booking:cancelled"
  | "lead:submitted"
  | "lead:claim_physician"
  | "consult:voucher_requested"
  | "consult:voucher_paid"
  | "consult:voucher_used"
  | "mre:quote_requested"
  | "mre:calculator_used"
  | "mind:questionnaire_completed"
  | "mind:consult_requested"
  | "corporate:lead_submitted"
  | "newsletter:subscribed"
  | "search:performed"
  | "search:empty"
  | "physician:viewed"
  | "physician:favorited"
  | "physician:contact_whatsapp"
  | "physician:contact_phone"
  // Funnel "Appeler le cabinet" pour médecins non-inscrits (tier free/unclaimed) :
  // call_intent = clic sur bouton "Appeler" → ouvre modal explicative
  // call_dismissed = modal fermée sans clic sur tel:
  // contact_phone (existant) = clic effectif sur tel: (bottom funnel)
  | "physician:call_intent"
  | "physician:call_dismissed"
  | "article:read"
  | "app:prereg_submitted"
  | "tool:used"
  | "ad:impression"
  | "ad:click"
  | "page:error_shown"
  // Form submit générique — tracking client après data.success de chaque
  // <form>. Nécessaire car GA4 Enhanced Measurement form_submit ne fire pas
  // sur forms avec e.preventDefault() + fetch (notre pattern). Audit
  // 2026-06-13 : form_submit GA4 = 0 events depuis 48h malgré fix
  // name="..." (qui n'était que la moitié du problème).
  | "form:submitted";

type EventProps = Record<string, string | number | boolean | undefined | null>;

type PlausibleFn = (
  event: string,
  options?: { props?: EventProps; callback?: () => void },
) => void;

type GtagFn = (
  command: "event" | "config",
  target: string,
  params?: Record<string, unknown>,
) => void;

declare global {
  interface Window {
    plausible?: PlausibleFn;
    gtag?: GtagFn;
  }
}

function isBrowser() {
  return typeof window !== "undefined";
}

/** Tracke un événement côté Plausible + GA4 + PostHog si présents. No-op sinon.
 *
 * GA4 `transport_type: "beacon"` force l'usage de `navigator.sendBeacon()`
 * sous le capot — la requête est envoyée même si la page se ferme/bascule
 * en arrière-plan dans la milliseconde qui suit. Critique pour tous les
 * `<a target="_blank">` (WhatsApp, tel:) et les form submit qui faisaient
 * perdre les events sans cette config (cf 30j GA4 mai 2026 :
 * physician_contact_whatsapp = 0 events alors que track() est wired). */
export function track(event: SahhaEvent, props?: EventProps): void {
  if (!isBrowser()) return;
  const cleanedProps = props
    ? Object.fromEntries(
        Object.entries(props).filter(([, v]) => v !== undefined && v !== null),
      )
    : undefined;

  try {
    window.plausible?.(event, cleanedProps ? { props: cleanedProps as EventProps } : undefined);
  } catch {
    // ignore — analytics ne doit jamais casser une app
  }

  try {
    window.gtag?.("event", event.replace(/:/g, "_"), {
      ...cleanedProps,
      transport_type: "beacon",
    });
  } catch {
    // ignore
  }

  try {
    // PostHog garde le ":" dans les event names → meilleur regroupement (booking:* funnel).
    if (posthog.__loaded) posthog.capture(event, cleanedProps);
  } catch {
    // ignore
  }

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[analytics]", event, cleanedProps);
  }
}

/** Tracke une vue de page manuelle (utile pour virtuel routing). */
export function pageView(url?: string): void {
  if (!isBrowser()) return;
  try {
    window.plausible?.("pageview", url ? { props: { url } } : undefined);
  } catch {}
  try {
    const measurementId = process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID;
    if (measurementId) {
      window.gtag?.("config", measurementId, {
        page_path: url ?? window.location.pathname,
      });
    }
  } catch {}
}

/** Événements d'engagement outbound (liens externes, WhatsApp, tel:). */
export function outbound(href: string, kind: "whatsapp" | "phone" | "external" = "external"): void {
  track("ad:click", { href, kind });
}
